/**
 * The stream filter on the post engagement page.
 *
 * Pure so the two rules that are easy to break silently can be pinned: the bucket for posts with
 * no visible stream, and the fact that an empty selection means *everything* rather than nothing.
 * Both fail quietly in a UI — the page just shows a different number of rows — which is exactly
 * the kind of bug a render test would not catch either.
 */

/**
 * Stands in for "this post has no stream I can see".
 *
 * `stream` is nullable for a real reason: a post from an organisation whose streams the viewer
 * cannot read resolves to nothing (see `StreamBadge`), and this page spans every organisation
 * the viewer can see. Without a bucket for them, picking any stream would make those rows vanish
 * with no way to ask for them back. Stream ids are positive, so a negative sentinel cannot
 * collide with a real one.
 */
export const NO_STREAM = -1;

export interface StreamFilterOption {
    key: number;
    value: number;
    text: string;
}

interface HasStream {
    stream?: { id: number; name: string } | null;
}

/**
 * The streams represented in these posts, each with its count.
 *
 * Built from the loaded rows rather than from the `streams` query on purpose: every option then
 * selects something. An option for a stream that published nothing in the period would let
 * somebody filter to an empty page and go looking for the fault in the data.
 */
export const streamFilterOptions = (posts: HasStream[]): StreamFilterOption[] => {
    const counts = new Map<number, { name: string; count: number }>();

    for (const post of posts) {
        const id = post.stream?.id ?? NO_STREAM;
        const name = post.stream?.name ?? 'No stream visible to you';
        const seen = counts.get(id);
        if (seen == null) {
            counts.set(id, { name, count: 1 });
        } else {
            seen.count += 1;
        }
    }

    return Array.from(counts.entries())
        // Named streams alphabetically; the "no stream" bucket last, because it is a remainder
        // rather than a choice anybody goes looking for.
        .sort(([aId, a], [bId, b]) => {
            if (aId === NO_STREAM) return 1;
            if (bId === NO_STREAM) return -1;
            return a.name.localeCompare(b.name);
        })
        .map(([id, { name, count }]) => ({ key: id, value: id, text: `${name} (${count})` }));
};

/** An empty selection means every stream — the filter narrows, and never has to be set. */
export const filterByStreams = <T extends HasStream>(posts: T[], streamIds: number[]): T[] =>
    streamIds.length === 0
        ? [...posts]
        : posts.filter((p) => streamIds.includes(p.stream?.id ?? NO_STREAM));

/**
 * Drop selections that no longer exist, returning the same array when nothing changed so this
 * can be used in a `setState` without looping.
 *
 * A stream chosen under "last 90 days" may have published nothing in "last 7". Left selected, it
 * would filter every row away while the dropdown still showed a reasonable-looking choice.
 */
export const pruneSelection = (selected: number[], options: StreamFilterOption[]): number[] => {
    const available = new Set(options.map((o) => o.value));
    const kept = selected.filter((id) => available.has(id));
    return kept.length === selected.length ? selected : kept;
};
